import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { slugCardapio } from '../utils/empresa';
import { categoryService, Product, Category, Order, ComandaPublica } from '../services';
import PedidoStatus from '../components/PedidoStatus';
import ModalConfirmacao from '../components/ModalConfirmacao';

interface CartItem {
  product: Product;
  quantity: number;
  notes: string;
}

interface TenantPublico {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
}

const COMANDA_ATIVA_STATUS = ['PENDING', 'CONFIRMED', 'PREPARING', 'READY', 'DELIVERED', 'REQUESTED_BILL'];

export default function Menu() {
  const [categories, setCategories] = useState<(Category & { products: Product[] })[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // T136: erro real da carga (ex: cardápio sem restaurante identificado) —
  // antes virava tela silenciosa de "cardápio vazio"
  const [erroCarga, setErroCarga] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  // Pedido/comanda ativo desta mesa (para acompanhar status e continuar pedindo)
  const [pedidoAtivo, setPedidoAtivo] = useState<Order | null>(null);
  const [mostrarStatus, setMostrarStatus] = useState(false);
  // Comandas abertas da mesa aguardando escolha do cliente (contas separadas)
  const [comandasDaMesa, setComandasDaMesa] = useState<ComandaPublica[] | null>(null);
  // Comanda escolhida por este aparelho para continuar pedindo
  const [mergeAlvoId, setMergeAlvoId] = useState<string | null>(null);
  // T215: "pedir a conta" pelo celular do cliente usa o modal do sistema
  const [confirmandoConta, setConfirmandoConta] = useState(false);
  const [formError, setFormError] = useState('');
  // Cliente pediu explicitamente para abrir comanda nova (conta separada)
  const [forcarComandaNova, setForcarComandaNova] = useState(false);
  // Comanda anterior ao "Separar conta" (para poder desfazer)
  const [comandaAnterior, setComandaAnterior] = useState<Order | null>(null);
  const [busca, setBusca] = useState('');
  // Categoria visível no momento (para destacar no menu rolante)
  const [categoriaAtiva, setCategoriaAtiva] = useState<string | null>(null);
  // Restaurante aberto no cardápio público (logo + nome no topo)
  const [tenantPublico, setTenantPublico] = useState<TenantPublico | null>(null);
  const barraRef = useRef<HTMLDivElement | null>(null);

  // Busca o restaurante da URL (?restaurante=...) p/ logo/nome do topo
  useEffect(() => {
    const slug = slugCardapio();
    if (!slug) return;
    api
      .get<{ data: TenantPublico }>('/tenants/publico', { params: { slug } })
      .then((res) => setTenantPublico(res.data.data))
      .catch(() => {});
  }, []);

  // Normaliza acentos para a busca achar "agua" = "água" no celular
  const normalizar = (texto: string) =>
    texto
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const buscaNormalizada = normalizar(busca);
  const filtrarProdutos = (produtos: Product[]) => {
    if (!buscaNormalizada) return produtos;
    return produtos.filter(
      (p) =>
        normalizar(p.name).includes(buscaNormalizada) ||
        normalizar(p.description ?? '').includes(buscaNormalizada),
    );
  };
  const categoriasVisiveis = categories
    .map((cat) => ({ ...cat, products: filtrarProdutos(cat.products) }))
    .filter((cat) => !buscaNormalizada || cat.products.length > 0);

  // Observa as seções de categoria para marcar a ativa no menu rolante
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = entry.target.id.replace('categoria-', '');
            if (id) setCategoriaAtiva(id);
          }
        }
      },
      { rootMargin: '-130px 0px -55% 0px' },
    );
    document.querySelectorAll('[id^="categoria-"]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [categoriasVisiveis]);

  // Rola suavemente até a categoria clicada
  const rolarParaCategoria = (categoryId: string) => {
    document.getElementById(`categoria-${categoryId}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  // Mesa identificada pelo QR Code (?mesa=N na URL) — fica travada, o cliente
  // não consegue trocar, garantindo que o pedido chegue na mesa certa.
  const [searchParams] = useSearchParams();
  const mesaDaUrl = useMemo(() => {
    const raw = searchParams.get('mesa');
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isInteger(n) && n >= 1 && n <= 99 ? String(n) : null;
  }, [searchParams]);

  useEffect(() => {
    // Nome lembrado deste aparelho (não precisa digitar toda vez)
    const nomeSalvo = localStorage.getItem('cardapio:nome');
    if (nomeSalvo) setCustomerName(nomeSalvo);

    loadCategories();
    if (mesaDaUrl) {
      setTableNumber(mesaDaUrl);
      carregarPedidoDaMesa(mesaDaUrl);
    }
  }, []);

  /** Rastreia o pedido da mesa (localStorage) e mostra a escolha de comanda */
  const carregarPedidoDaMesa = async (mesa: string) => {
    const chave = `cardapio:pedido:${mesa}`;
    const id = localStorage.getItem(chave);
    if (id) {
      try {
        const { orderService } = await import('../services');
        const r = await orderService.findById(id);
        const pedido = r.data.data;
        if (COMANDA_ATIVA_STATUS.includes(pedido.status)) {
          setPedidoAtivo(pedido);
          setMergeAlvoId(pedido.id);
          // T213: o carrinho começa VAZIO — ele representa só o que vai ser
          // lançado agora. Antes ele espelhava a comanda inteira e o cliente
          // podia remover item já lançado (que a cozinha já estava fazendo).
          setCart([]);
          return;
        }
        // comanda finalizada: limpa o rastreio deste aparelho
        localStorage.removeItem(chave);
      } catch {
        localStorage.removeItem(chave);
      }
    }
    // Sem rastreio local (ex: outro aparelho): mostra as comandas abertas da
    // mesa para o cliente escolher continuar uma existente ou abrir outra.
    await comandasAbertasDaMesa(mesa);
  };

  const comandasAbertasDaMesa = async (mesa: string) => {
    try {
      const { orderService } = await import('../services');
      const response = await orderService.findOpenByTable(parseInt(mesa, 10));
      const abertas = response.data.data;
      if (abertas.length > 0) {
        setComandasDaMesa(abertas);
      }
    } catch {
      // sem comandas — segue o fluxo normal
    }
  };

  /** Abre o modal com as comandas abertas da mesa (para conferir/entrar) */
  const verComandasDaMesa = async () => {
    if (!tableNumber) return;
    const { orderService } = await import('../services');
    const response = await orderService.findOpenByTable(parseInt(tableNumber, 10));
    const abertas = response.data.data;
    const comandaAtual = pedidoAtivo;
    // T134: a comanda "deste aparelho" entra na lista no mesmo formato resumido
    const atualResumo: ComandaPublica | null = comandaAtual
      ? {
          id: comandaAtual.id,
          nome: String(comandaAtual.customer_name ?? '').trim().split(/\s+/)[0],
          itens: comandaAtual.items.filter((i) => !i.cancelado).length,
          total: Number(comandaAtual.total),
          status: comandaAtual.status,
        }
      : null;
    setComandasDaMesa(
      abertas.some((c) => c.id === comandaAtual?.id)
        ? abertas
        : atualResumo && COMANDA_ATIVA_STATUS.includes(atualResumo.status)
          ? [atualResumo, ...abertas]
          : abertas,
    );
  };

  /** Cliente escolheu entrar numa comanda aberta da mesa */
  const entrarEmComanda = async (comanda: ComandaPublica) => {
    const chave = `cardapio:pedido:${tableNumber ?? ''}`;
    localStorage.setItem(chave, comanda.id);
    setMergeAlvoId(comanda.id);
    setComandasDaMesa(null);
    try {
      // T134: a lista pública traz só o resumo (id/nome/itens/total) — o
      // detalhe da comanda é buscado por id, como o próprio aparelho do cliente
      const { orderService } = await import('../services');
      const r = await orderService.findById(comanda.id);
      const detalhe = r.data.data;
      setPedidoAtivo(detalhe);
      // T213: carrinho vazio ao entrar numa comanda existente (só itens novos)
      setCart([]);
    } catch {
      // sem detalhe: cliente segue na comanda escolhida podendo lançar novos itens
    }
  };

  /** Cliente quer conta separada: carrinho vazio, sem vínculo com comandas existentes */
  const criarComandaSeparada = () => {
    // Guarda a comanda atual para permitir "Cancelar separação"
    setComandaAnterior(pedidoAtivo);
    // Outra pessoa assume o aparelho — não pré-carrega o nome anterior
    setCustomerName('');
    if (tableNumber) {
      localStorage.removeItem(`cardapio:pedido:${tableNumber}`);
    }
    setForcarComandaNova(true);
    setMergeAlvoId(null);
    setPedidoAtivo(null);
    setCart([]);
    setComandasDaMesa(null);
  };

  /** Volta atrás da separação: restaura a comanda anterior */
  const cancelarSeparacao = () => {
    const comanda = comandaAnterior;
    setComandaAnterior(null);
    setForcarComandaNova(false);
    if (!comanda) return;
    setPedidoAtivo(comanda);
    setMergeAlvoId(comanda.id);
    // T213: volta para a comanda anterior com o carrinho vazio (só itens novos)
    setCart([]);
    if (comanda.table_number) {
      localStorage.setItem(`cardapio:pedido:${comanda.table_number}`, comanda.id);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoryService.findAllActive();
      setCategories(response.data.data);
      setErroCarga('');
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setErroCarga(
        msg ||
          'Não foi possível carregar o cardápio. Verifique a conexão e tente novamente.',
      );
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const addToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }
      return [...prev, { product, quantity: 1, notes: '' }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity < 1) {
      removeFromCart(productId);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item,
      ),
    );
  };

  const cartTotal = cart.reduce(
    (sum, item) => sum + item.product.price * item.quantity,
    0,
  );

  /** T135 — observação do item ("sem cebola", "bem passado") direto do celular */
  const setItemNotes = (productId: string, notes: string) => {
    setCart((prev) =>
      prev.map((item) => (item.product.id === productId ? { ...item, notes } : item)),
    );
  };

  const SUGESTOES_OBSERVACAO = ['Sem cebola', 'Sem gelo', 'Bem passado', 'Sem sal', 'Ao ponto'];

  const handlePlaceOrder = async () => {
    if (!customerName.trim()) {
      setFormError('Por favor, informe seu nome');
      return;
    }

    if (cart.length === 0) {
      setFormError('Seu carrinho está vazio');
      return;
    }

    setFormError('');
    try {
      const { orderService } = await import('../services');
      const order = (
        await orderService.create({
          customer_name: customerName,
          table_number: tableNumber ? parseInt(tableNumber) : undefined,
          items: cart.map((item) => ({
            product_id: item.product.id,
            quantity: item.quantity,
            notes: item.notes || undefined,
          })),
          merge_into_order_id: mergeAlvoId ?? undefined,
          // T213: entrando numa comanda existente o envio é sempre SOMA — só os
          // itens novos do carrinho entram; o que já foi lançado não é tocado.
          adicionar_itens: mergeAlvoId ? true : undefined,
          criar_comanda_nova: forcarComandaNova || undefined,
        })
      ).data.data;

      setPedidoAtivo(order);
      setMergeAlvoId(null);
      setForcarComandaNova(false);
      if (tableNumber) {
        localStorage.setItem(`cardapio:pedido:${tableNumber}`, order.id);
      }
      setMostrarStatus(true);
      setShowCart(false);
      setCart([]);
      // Lembra o nome deste aparelho para os próximos pedidos
      if (customerName.trim()) {
        localStorage.setItem('cardapio:nome', customerName.trim());
      }
    } catch (error) {
      console.error('Erro ao criar pedido:', error);
      const msg = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setFormError(msg || 'Erro ao criar pedido. Tente novamente.');
    }
  };

  // Cliente pede a conta (marca a comanda para o garçom fechar) — T215: o
  // modal do sistema no lugar da janela nativa do navegador
  const pedirConta = () => {
    if (!pedidoAtivo) return;
    setConfirmandoConta(true);
  };

  const confirmarPedidoConta = async () => {
    if (!pedidoAtivo) return;
    try {
      const { orderService } = await import('../services');
      const r = await orderService.updateStatus(pedidoAtivo.id, { status: 'REQUESTED_BILL' });
      setPedidoAtivo(r.data.data);
    } catch {
      // silencioso
    } finally {
      setConfirmandoConta(false);
    }
  };

  // Mantém o banner da comanda em andamento atualizado em tempo quase-real
  // (ex: pedido entregue pelo garçom vira "Tudo entregue" sem precisar recarregar)
  useEffect(() => {
    if (!pedidoAtivo?.id || mostrarStatus) return;
    let ativo = true;
    const id = setInterval(async () => {
      try {
        const { orderService } = await import('../services');
        const r = await orderService.findById(pedidoAtivo.id);
        if (!ativo) return;
        setPedidoAtivo(r.data.data);
      } catch {
        // tentará de novo no próximo tick
      }
    }, 4000);
    return () => {
      ativo = false;
      clearInterval(id);
    };
  }, [pedidoAtivo?.id, mostrarStatus]);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Carregando cardápio...</p>
      </div>
    );
  }

  // T136: cardápio não identificado / sem conexão — tela explícita em vez de
  // lista vazia ("parece que não tem nada no menu")
  if (erroCarga) {
    return (
      <div className="max-w-md mx-auto text-center py-12 px-4">
        <p className="text-3xl mb-3">🍽️</p>
        <h1 className="text-xl font-bold text-gray-100 mb-2">Cardápio indisponível</h1>
        <p className="text-gray-400 mb-6">{erroCarga}</p>
        <button
          onClick={() => {
            setIsLoading(true);
            setErroCarga('');
            loadCategories();
          }}
          className="btn btn-primary"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  if (mostrarStatus && pedidoAtivo) {
    return (
      <PedidoStatus
        pedidoInicial={pedidoAtivo}
        onVoltar={() => {
          setMostrarStatus(false);
          setShowCart(false);
          // T213: "Ver Cardápio" volta com o carrinho VAZIO — o que já foi
          // lançado não é tocado; o próximo envio soma só o item novo.
          if (pedidoAtivo && COMANDA_ATIVA_STATUS.includes(pedidoAtivo.status)) {
            setMergeAlvoId(pedidoAtivo.id);
          }
          setCart([]);
        }}
        onNovoPedido={async () => {
          setMostrarStatus(false);
          setShowCart(false);
          // Consulta o status REAL no backend — o polling interno do
          // PedidoStatus pode estar à frente do estado local do Menu.
          let statusAtual = pedidoAtivo?.status;
          try {
            const { orderService } = await import('../services');
            const atualizado = await orderService.findById(pedidoAtivo!.id);
            statusAtual = atualizado.data.data.status;
            setPedidoAtivo(atualizado.data.data);
            // T213: religa o alvo do merge com o carrinho VAZIO — o carrinho do
            // cliente só leva o que é novo; o que já foi lançado não é tocado.
            if (COMANDA_ATIVA_STATUS.includes(statusAtual)) {
              setMergeAlvoId(atualizado.data.data.id);
            }
            setCart([]);
          } catch {
            // sem conexão: usa o status local como fallback (T213: carrinho vazio)
            if (statusAtual && COMANDA_ATIVA_STATUS.includes(statusAtual)) {
              setMergeAlvoId(pedidoAtivo!.id);
            }
            setCart([]);
          }
          // Comanda ainda em andamento: mantém o rastreio — o banner
          // "Comanda em andamento" continua e o novo pedido entra nela.
          // Comanda finalizada: começa um ciclo novo do zero.
          if (!statusAtual || !COMANDA_ATIVA_STATUS.includes(statusAtual)) {
            setPedidoAtivo(null);
            if (tableNumber) localStorage.removeItem(`cardapio:pedido:${tableNumber}`);
          }
        }}
        onFecharResumo={() => {
          // ~5 min após a conta fechada: o recibo some e a comanda sai da mesa
          setPedidoAtivo(null);
          setShowCart(false);
          setCart([]);
          setForcarComandaNova(false);
          setMergeAlvoId(null);
          if (tableNumber) localStorage.removeItem(`cardapio:pedido:${tableNumber}`);
        }}
      />
    );
  }

  return (
    <div className="space-y-8 pb-28">
      <div className="text-center">
        {tenantPublico?.logo_url && (
          <img
            src={tenantPublico.logo_url}
            alt={tenantPublico.name}
            className="w-24 h-24 rounded-2xl object-cover mx-auto mb-3 border border-gray-800"
          />
        )}
        {tenantPublico && (
          <p className="text-primary-400 font-semibold mb-1">{tenantPublico.name}</p>
        )}
        {tenantPublico?.slug === 'restaurante-demo' && (
          <div className="mb-3">
            <a
              href="/login"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-950 bg-primary-400 hover:bg-primary-300 rounded-full px-5 py-2 transition-colors"
            >
              🎬 Testar o painel do restaurante
            </a>
            <p className="text-[11px] text-gray-500 mt-1.5">
              Ambiente de demonstração — use as contas de exemplo
            </p>
          </div>
        )}
        <h1 className="text-4xl font-bold text-gray-100 mb-2">Nosso Cardápio</h1>
        <p className="text-gray-400 mb-6">Escolha seus pratos favoritos</p>

        <div className="max-w-md mx-auto">
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="input-field"
            placeholder="🔍 Procurar prato..."
          />
        </div>
      </div>

      {categoriasVisiveis.length > 0 && (
        <div
          ref={barraRef}
          className="sticky top-0 z-30 relative -mx-4 px-4 py-3 bg-gray-950/95 backdrop-blur border-b border-gray-800"
        >
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {categoriasVisiveis.map((category) => (
              <button
                key={category.id}
                onClick={() => rolarParaCategoria(category.id)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  categoriaAtiva === category.id
                    ? 'bg-primary-400 text-gray-950'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
          {/* dica visual de que há mais categorias para rolar */}
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-gray-950 to-transparent" />
        </div>
      )}

      {forcarComandaNova && (
        <div className="card p-4 border-orange-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-orange-300">🧾 Nova comanda aguardando</p>
            <p className="text-sm text-gray-400">
              Seu próximo pedido abrirá uma comanda nova. Os pedidos em andamento
              continuam normalmente na cozinha.
            </p>
          </div>
          <button
            onClick={cancelarSeparacao}
            className="btn-secondary text-sm"
          >
            Voltar à comanda atual
          </button>
        </div>
      )}

      {pedidoAtivo && COMANDA_ATIVA_STATUS.includes(pedidoAtivo.status) && (
        <div className="card p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-primary-400 text-xl leading-none">🧾</span>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500">
                  Sua comanda
                </p>
                <p className="text-2xl font-extrabold text-primary-400 leading-tight">
                  {pedidoAtivo.table_number ? `Mesa ${pedidoAtivo.table_number}` : 'Balcão'}
                </p>
                {pedidoAtivo.customer_name && (
                  <p className="font-medium text-gray-100 leading-tight">
                    {pedidoAtivo.customer_name}
                  </p>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-100 leading-tight">
                R$ {Number(pedidoAtivo.total).toFixed(2)}
              </p>
              <p className="text-xs text-gray-400">
                {pedidoAtivo.items.length} {pedidoAtivo.items.length === 1 ? 'item' : 'itens'}
              </p>
            </div>
          </div>
          {/* T213: deixa claro que o que já foi lançado não muda por aqui */}
          <p className="text-xs text-gray-500 mt-2">
            Os itens já lançados ficam como estão — toque em <strong>Adicionar</strong> no
            cardápio para incluir mais neste pedido.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {pedidoAtivo.status === 'DELIVERED' ? (
              <>
                <div className="w-full flex items-center justify-center gap-1.5 text-green-400 text-sm font-medium py-1">
                  ☑️ Tudo entregue — bom apetite!
                </div>
                <button
                  onClick={() => setMostrarStatus(true)}
                  className="btn-primary flex-1"
                >
                  📋 Ver meus pedidos
                </button>
                <button onClick={pedirConta} className="btn-secondary">
                  🧾 Pedir a conta
                </button>
              </>
            ) : pedidoAtivo.status === 'REQUESTED_BILL' ? (
              <div className="flex-1 flex items-center justify-center text-orange-300 text-sm font-medium py-2">
                🧾 Conta solicitada — o garçom está a caminho
              </div>
            ) : (
              <button
                onClick={() => setMostrarStatus(true)}
                className="btn-primary flex-1"
              >
                📋 Ver pedidos ·{' '}
                {pedidoAtivo.items.filter((i) => !i.cancelado).reduce((a, i) => a + i.quantity, 0) === 1
                  ? '1 pendente'
                  : `${pedidoAtivo.items.filter((i) => !i.cancelado).reduce((a, i) => a + i.quantity, 0)} pendentes`}
              </button>
            )}
            <button
              onClick={verComandasDaMesa}
              className="btn-secondary"
            >
              🪑 Comandas
            </button>
            <button
              onClick={criarComandaSeparada}
              className="btn-secondary"
            >
              ➕ Nova comanda
            </button>
          </div>
        </div>
      )}

      {categoriasVisiveis.map((category) => (
        <div key={category.id} id={`categoria-${category.id}`} className="scroll-mt-28">
          <h2 className="text-2xl font-bold text-gray-100 mb-4">{category.name}</h2>
          {category.description && (
            <p className="text-gray-400 mb-4">{category.description}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {category.products.map((product) => (
              <div key={product.id} className="card hover:shadow-md transition-shadow">
                {product.image_url && (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className={`w-full h-48 object-cover rounded-lg mb-4 ${
                      product.is_available ? '' : 'opacity-40 grayscale'
                    }`}
                  />
                )}
                <h3 className="text-lg font-semibold text-gray-100">{product.name}</h3>
                {product.description && (
                  <p className="text-sm text-gray-400 mt-1">{product.description}</p>
                )}
                <div className="flex items-center justify-between mt-4">
                  <span className="text-xl font-bold text-primary-400">
                    R$ {Number(product.price).toFixed(2)}
                  </span>
                  {product.is_available ? (
                    <button
                      onClick={() => addToCart(product)}
                      className="btn-primary text-sm"
                    >
                      Adicionar
                    </button>
                  ) : (
                    <span className="px-3 py-1 rounded bg-gray-700 text-gray-400 text-sm font-medium">
                      Esgotado
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {comandasDaMesa && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="card w-full max-w-md">
            <div className="flex justify-between items-start mb-2">
              <h2 className="text-2xl font-bold text-gray-100">
                {tableNumber ? `Comandas da mesa ${tableNumber}` : 'Comanda aberta'}
              </h2>
              <button
                onClick={() => setComandasDaMesa(null)}
                className="text-gray-400 hover:text-gray-200 text-xl leading-none"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Escolha: continuar numa comanda existente ou criar uma nova comanda.
            </p>

            <div className="space-y-3 mb-4">
              {comandasDaMesa.map((comanda) => (
                <div
                  key={comanda.id}
                  className="flex items-center justify-between gap-3 bg-gray-800 border border-gray-700 rounded-lg p-3"
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-100 truncate">
                      Comanda de {comanda.nome}
                      {pedidoAtivo?.id === comanda.id && (
                        <span className="ml-2 text-xs font-medium text-primary-300 bg-primary-900/30 px-2 py-0.5 rounded-full">
                          Sua comanda
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-400">
                      {comanda.itens} itens ·{' '}
                      R$ {Number(comanda.total).toFixed(2)}
                    </p>
                  </div>
                  <button
                    onClick={() => entrarEmComanda(comanda)}
                    className="btn-primary text-sm whitespace-nowrap"
                  >
                    {pedidoAtivo?.id === comanda.id ? 'Acompanhar' : 'Entrar'}
                  </button>
                </div>
              ))}
            </div>

            <button onClick={criarComandaSeparada} className="btn-secondary w-full">
              ➕ Criar nova comanda
            </button>
          </div>
        </div>
      )}

      {buscaNormalizada && categoriasVisiveis.length === 0 && (
        <div className="card text-center py-10">
          <p className="text-gray-400">Nenhum prato encontrado para "{busca.trim()}"</p>
        </div>
      )}

      {cart.length > 0 && (
        <div className="fixed bottom-4 right-4">
          <button
            onClick={() => setShowCart(!showCart)}
            className="btn-primary shadow-lg"
          >
            Carrinho ({cart.reduce((sum, item) => sum + item.quantity, 0)}) - R${' '}
            {cartTotal.toFixed(2)}
          </button>
        </div>
      )}

      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Seu Pedido</h2>
              <button
                onClick={() => {
                  setShowCart(false);
                  setFormError('');
                }}
                className="text-gray-400 hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {cart.map((item) => (
                <div key={item.product.id} className="border-b pb-4">
                  <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold">{item.product.name}</h3>
                    <p className="text-sm text-gray-400">
                      R$ {Number(item.product.price).toFixed(2)} cada
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                      className="btn-secondary text-sm px-2 py-1"
                    >
                      -
                    </button>
                    <span className="w-8 text-center">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                      className="btn-secondary text-sm px-2 py-1"
                    >
                      +
                    </button>
                    <button
                      onClick={() => removeFromCart(item.product.id)}
                      className="btn-danger text-sm px-2 py-1 ml-2"
                    >
                      ✕
                    </button>
                  </div>
                  </div>

                  {/* T135: observação do item — antes só dava para observar o pedido inteiro */}
                  <div className="mt-2">
                    <input
                      type="text"
                      value={item.notes}
                      onChange={(e) => setItemNotes(item.product.id, e.target.value)}
                      maxLength={140}
                      placeholder="Alguma observação? (sem cebola, bem passado...)"
                      className="input-field text-sm"
                    />
                    <div className="flex flex-wrap gap-1 mt-1">
                      {SUGESTOES_OBSERVACAO.map((sugestao) => (
                        <button
                          key={sugestao}
                          type="button"
                          onClick={() => setItemNotes(item.product.id, sugestao)}
                          className="text-[11px] px-2 py-0.5 rounded-full bg-gray-700/60 hover:bg-gray-700 text-gray-300"
                        >
                          {sugestao}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-4 mb-6">
              <div className="flex justify-between text-xl font-bold">
                <span>Total:</span>
                <span className="text-primary-400">R$ {cartTotal.toFixed(2)}</span>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Seu Nome *
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="input-field"
                  placeholder="João Silva"
                />
              </div>
              {mesaDaUrl ? (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Mesa
                  </label>
                  <div className="input-field flex items-center justify-between">
                    <span className="font-semibold">Mesa {mesaDaUrl}</span>
                    <span className="text-xs text-green-400 bg-green-900/40 px-2 py-0.5 rounded-full">
                      ✓ Identificada pelo QR Code
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    A mesa já foi identificada — é só fazer o pedido.
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Número da Mesa
                  </label>
                  <input
                    type="number"
                    value={tableNumber}
                    onChange={(e) => setTableNumber(e.target.value)}
                    className="input-field"
                    placeholder="1"
                  />
                </div>
              )}
            </div>

            {formError && (
              <p className="text-red-400 text-sm mb-3">{formError}</p>
            )}

            <button onClick={handlePlaceOrder} className="btn-primary w-full">
              Confirmar Pedido
            </button>
          </div>
        </div>
      )}

      {/* T215: pedir a conta com o modal do sistema (não a janela do navegador) */}
      {confirmandoConta && pedidoAtivo && (
        <ModalConfirmacao
          titulo="Pedir a conta"
          subtitulo="O garçom leva a conta até a mesa"
          resumo={[
            pedidoAtivo.table_number ? `Mesa ${pedidoAtivo.table_number}` : 'Balcão',
            `${pedidoAtivo.items
              .filter((i) => !i.cancelado)
              .reduce((a, i) => a + i.quantity, 0)} item(ns)`,
            `Total: R$ ${Number(pedidoAtivo.total).toFixed(2)}`,
          ]}
          rotuloConfirmar="Pedir a conta"
          onConfirmar={confirmarPedidoConta}
          onCancelar={() => setConfirmandoConta(false)}
        />
      )}
    </div>
  );
}
